import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Typography,
  Button,
  Alert,
  Chip,
  Tooltip,
  Divider,
  IconButton,
} from '@mui/material';
import {
  Refresh as RefreshIcon,
  VisibilityOff as HideIcon,
  Visibility as ShowIcon,
  Group as FollowersIcon,
  PersonAdd as FollowingIcon,
  Article as PostsIcon,
  TrendingUp as UpIcon,
  TrendingDown as DownIcon,
} from '@mui/icons-material';
import { useAuth } from '../contexts/AuthContext';
import { SectionCard } from './SectionCard';
import { SectionSkeleton } from './SectionSkeleton';
import { getPlatformColor, getPlatformName } from '../config/platforms';

const API_URL = process.env.REACT_APP_API_URL || '';

interface Account {
  platform: string;
  /** The platform's own id, used as the hide key. */
  account_ref: string;
  hidden: boolean;
  account: string;
  followers: number | null;
  following: number | null;
  posts: number | null;
  /**
   * Platform-specific figures with no column of their own. X reports likes given and list
   * memberships; LinkedIn reports page-level reach, which is the only impressions figure the
   * app has now that Facebook removed theirs.
   */
  extra: {
    likes?: number | null;
    listed?: number | null;
    impressions?: number | null;
    unique_impressions?: number | null;
    clicks?: number | null;
    engagement_rate?: number | null;
  } | null;
  /** Change since the oldest reading in the window, or null when there is nothing to compare. */
  followers_change: number | null;
  measured_at: string;
}

interface Summary {
  accounts: Account[];
  totals: {
    followers: number;
    following: number;
    posts: number;
    followers_change: number | null;
  };
  ever_measured: boolean;
  hidden_count: number;
}

const num = (n: number | null | undefined) => (n === null || n === undefined ? '—' : n.toLocaleString());

/**
 * Audience totals per connected account — followers, following, post counts.
 *
 * Separate from the rest of Analytics because it answers a different question: that page is
 * "how did this post do", this is "how big is the audience and is it growing". The two come
 * from different endpoints on every platform.
 *
 * Figures a platform does not report show as an em dash rather than zero. LinkedIn company
 * pages have no "following" count, and only X reports likes given — a zero there would read
 * as a real measurement.
 */
export const AudienceTotals: React.FC<{ days: number }> = ({ days }) => {
  const { token } = useAuth();
  const [data, setData] = useState<Summary | null>(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  // Showing hidden accounts is a view state, not a saved preference
  const [showHidden, setShowHidden] = useState(false);
  const [error, setError] = useState('');
  const [notice, setNotice] = useState('');

  const load = useCallback(async () => {
    try {
      const r = await fetch(`${API_URL}/api/audience?days=${days}&includeHidden=${showHidden}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!r.ok) throw new Error();
      setData(await r.json());
    } catch {
      setError('Could not load audience figures');
    } finally {
      setLoading(false);
    }
  }, [token, days, showHidden]);

  useEffect(() => {
    load();
  }, [load]);

  /** Hide or unhide one account. The whole list is sent, since that is what the endpoint stores. */
  /**
   * Hide every account this workspace does not publish to.
   *
   * A connection carries every Page and organization the login administers, so a workspace set
   * up for one company opens showing another's follower counts. Doing that one row at a time is
   * a dozen clicks per workspace, repeated whenever an account is added.
   */
  const hideOthers = async () => {
    setError(''); setNotice('');
    try {
      const r = await fetch(`${API_URL}/api/audience/hidden`, {
        method: 'PATCH',
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ onlyTargets: true }),
      });
      const body = await r.json();
      if (!r.ok) { setError(body.message || body.error || 'Could not update'); return; }
      setNotice(
        `Now showing only what this workspace publishes to. ${(body.hidden || []).length} other account(s) hidden — ` +
        'use "Show hidden" to see them.'
      );
      load();
    } catch {
      setError('Network error');
    }
  };

  const toggleHidden = async (account: Account) => {
    if (!data) return;
    const key = `${account.platform}:${account.account_ref}`;

    setError('');
    try {
      // Send just this account, never the whole list: the response omits hidden accounts
      // unless asked for, so rebuilding the list from what is on screen would unhide
      // everything the current view cannot see.
      const r = await fetch(`${API_URL}/api/audience/hidden`, {
        method: 'PATCH',
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify(account.hidden ? { unhide: [key] } : { hide: [key] }),
      });
      const body = await r.json();
      if (!r.ok) {
        setError(body.message || body.error || 'Could not update');
        return;
      }
      // Reload rather than trusting the PATCH response, which is always the unhidden view.
      //
      // Awaited before announcing the change: firing the event first sent every other panel
      // re-fetching while this reload was still in flight, so several requests raced and a
      // card could sit empty, or one panel could update while another still showed the old
      // list. The write has already committed server-side by this point, so every listener
      // that fetches after this sees the same state.
      await load();

      // Hiding an account changes what every audience panel should show, not just this one.
      // The charts, the posts-and-movement table and the demographics all filter on the same
      // server-side list, so they each need to re-fetch — without this they kept displaying a
      // hidden account until the page was reloaded.
      window.dispatchEvent(new CustomEvent('shoutvector:audience-hidden-changed'));
    } catch {
      setError('Network error updating hidden accounts');
    }
  };

  const refresh = async () => {
    setRefreshing(true);
    setError('');
    setNotice('');
    try {
      const r = await fetch(`${API_URL}/api/audience/refresh`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` },
      });
      const body = await r.json();
      if (!r.ok) {
        setError(body.error || 'Could not refresh');
        return;
      }
      setData(body);
      setNotice(body.message);
    } catch {
      setError('Network error refreshing audience figures');
    } finally {
      setRefreshing(false);
    }
  };

  if (loading) {
    return (
      <SectionSkeleton rows={4} />
    );
  }

  const totals = data?.totals;

  return (
    <SectionCard
      title="Audience"
      description="Followers and totals for every connected account."
      actions={<>
        {/* Offered only when there is something to hide — on a workspace whose accounts are
            all its own this would do nothing. */}
        {(data?.accounts?.length || 0) > 1 && (
          <Button size="small" onClick={hideOthers}>
            Show only this workspace
          </Button>
        )}
        <Button
          size="small"
          startIcon={<RefreshIcon />}
          disabled={refreshing}
          onClick={refresh}
        >
          {refreshing ? 'Refreshing…' : 'Refresh'}
        </Button>
      </>}
    >

        {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>{error}</Alert>}
        {notice && <Alert severity="success" sx={{ mb: 2 }} onClose={() => setNotice('')}>{notice}</Alert>}

        {!data?.ever_measured ? (
          <Alert severity="info">
            No audience figures recorded yet. Press Refresh to fetch them now — after that they
            update hourly.
          </Alert>
        ) : (
          <>
            {/* Totals across every account */}
            <Box
              sx={{
                display: 'grid',
                gridTemplateColumns: { xs: '1fr 1fr', sm: 'repeat(3, 1fr)' },
                gap: 2,
                mb: 3,
              }}
            >
              <Total
                icon={<FollowersIcon />}
                label="Followers"
                value={num(totals?.followers)}
                change={totals?.followers_change ?? null}
                days={days}
                colour="#1976d2"
              />
              <Total icon={<FollowingIcon />} label="Following" value={num(totals?.following)} colour="#9c27b0" />
              <Total icon={<PostsIcon />} label="Posts published" value={num(totals?.posts)} colour="#10b981" />
            </Box>

            <Divider sx={{ mb: 2 }} />

            {/* Per account, because a combined follower count hides which brand is growing */}
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
              {data.accounts.map(a => (
                <Box
                  key={`${a.platform}-${a.account_ref}`}
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 1.5,
                    flexWrap: 'wrap',
                    pb: 1.5,
                    borderBottom: 1,
                    borderColor: 'divider',
                    '&:last-of-type': { borderBottom: 0, pb: 0 },
                    // Dimmed rather than styled differently, so a hidden row reads as excluded
                    opacity: a.hidden ? 0.5 : 1,
                  }}
                >
                  <Chip
                    size="small"
                    label={getPlatformName(a.platform)}
                    sx={{ bgcolor: getPlatformColor(a.platform), color: '#fff', minWidth: { xs: 72, sm: 96 } }}
                  />
                  <Typography variant="body2" sx={{ fontWeight: 500, flexGrow: 1, minWidth: { xs: 0, sm: 140 } }}>
                    {a.account}
                  </Typography>

                  <Metric label="followers" value={a.followers} change={a.followers_change} />
                  <Metric label="following" value={a.following} />
                  <Metric label="posts" value={a.posts} />
                  {a.extra?.likes !== null && a.extra?.likes !== undefined && (
                    <Metric label="likes given" value={a.extra.likes} />
                  )}
                  {/* LinkedIn page reach — the only impressions figure available anywhere now */}
                  {a.extra?.impressions !== null && a.extra?.impressions !== undefined && (
                    <Metric label="impressions" value={a.extra.impressions} />
                  )}
                  {a.extra?.clicks !== null && a.extra?.clicks !== undefined && (
                    <Metric label="clicks" value={a.extra.clicks} />
                  )}
                  <Tooltip title={a.hidden ? 'Show in totals' : 'Hide from totals'} arrow>
                    <IconButton size="small" onClick={() => toggleHidden(a)}>
                      {a.hidden ? <ShowIcon fontSize="small" /> : <HideIcon fontSize="small" />}
                    </IconButton>
                  </Tooltip>
                </Box>
              ))}
            </Box>

            {(data.hidden_count > 0 || showHidden) && (
              <Button size="small" sx={{ mt: 1.5 }} onClick={() => setShowHidden(v => !v)}>
                {showHidden
                  ? 'Hide excluded accounts'
                  : `Show ${data.hidden_count} hidden account${data.hidden_count === 1 ? '' : 's'}`}
              </Button>
            )}

            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 2 }}>
              A dash means the platform does not report that figure. Only Instagram and X report
              a following count, only X reports likes given, and impressions come from
              LinkedIn company pages alone — Meta removed post-level impressions from the
              Graph API, and X requires elevated access for them. Hidden accounts are left out
              of the totals above.
            </Typography>
          </>
        )}
    </SectionCard>
  );
};

const Total: React.FC<{
  icon: React.ReactElement;
  label: string;
  value: string;
  change?: number | null;
  days?: number;
  colour: string;
}> = ({ icon, label, value, change, days, colour }) => (
  <Box sx={{ border: 1, borderColor: 'divider', borderRadius: 2, p: 2 }}>
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
      <Box sx={{ bgcolor: `${colour}20`, color: colour, borderRadius: 1.5, p: 0.75, display: 'flex' }}>
        {icon}
      </Box>
      <Typography variant="h5" component="div">{value}</Typography>
    </Box>
    <Typography variant="body2" color="text.secondary">{label}</Typography>
    {/* Only shown when there are two readings to compare, so it is never a misleading zero */}
    {change !== null && change !== undefined && (
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mt: 0.5 }}>
        <Box
          sx={{
            display: 'flex',
            color: change > 0 ? 'success.main' : change < 0 ? 'error.main' : 'text.disabled',
            '& svg': { fontSize: 16 },
          }}
        >
          {change >= 0 ? <UpIcon /> : <DownIcon />}
        </Box>
        <Typography
          variant="caption"
          sx={{ color: change > 0 ? 'success.main' : change < 0 ? 'error.main' : 'text.secondary' }}
        >
          {change > 0 ? '+' : ''}{change.toLocaleString()} in {days} days
        </Typography>
      </Box>
    )}
  </Box>
);

const Metric: React.FC<{ label: string; value: number | null; change?: number | null }> = ({
  label,
  value,
  change,
}) => (
  <Tooltip title={label} arrow>
    <Box sx={{ textAlign: 'right', minWidth: 78 }}>
      <Typography variant="body2" sx={{ fontWeight: 600 }}>
        {num(value)}
        {change !== null && change !== undefined && change !== 0 && (
          <Typography
            component="span"
            variant="caption"
            sx={{ ml: 0.5, color: change > 0 ? 'success.main' : 'error.main' }}
          >
            {change > 0 ? '+' : ''}{change}
          </Typography>
        )}
      </Typography>
      <Typography variant="caption" color="text.secondary">{label}</Typography>
    </Box>
  </Tooltip>
);
